'use client'

import { C, Callout, Exercise, H, Lesson, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/code-basics'

export default function ObjectsAndClasses() {
  return (
    <Lesson>
      <Step>
        <H>Bundling values together</H>
        <P>
          An <strong>object</strong> groups related values under one name, each with its own label (called a{' '}
          <strong>property</strong>). A house has a size, a number of rooms and a price — so make it one thing:{' '}
          <C>{'{ size: 120, rooms: 3, price: 250000 }'}</C>, and read a property with a dot: <C>house.price</C>.
        </P>
        <Exercise id="objects-intro" title="Objects and datasets" {...EX.objectsIntro} />
        <P>
          That’s what a <strong>dataset</strong> really is: a list of objects, each one an example. Those four dots are the kind
          of data you’ll fit your first model to in Module 6.
        </P>
      </Step>

      <Step gate="mean-of">
        <H>Working with a dataset</H>
        <P>
          A <C>type</C> describes the shape of an object, so the editor can check you’re using it correctly:{' '}
          <C>{'type House = { size: number; price: number }'}</C>. Try typing <C>h.</C> inside a loop below — autocomplete knows
          exactly which properties a house has.
        </P>
        <Exercise
          id="mean-of"
          title="Average price, biggest house"
          {...EX.meanOf}
          hints={[
            <>
              Add up <C>h.price</C> for every house, then divide by <C>hs.length</C>.
            </>,
            <>
              For the biggest: keep a <C>best</C> house and replace it whenever <C>h.size &gt; best.size</C>.
            </>,
          ]}
        />
      </Step>

      <Step>
        <H>Classes: blueprints with behaviour</H>
        <P>
          A <strong>class</strong> is a blueprint for objects that carry both data <em>and</em> functions that work on that data
          (functions attached to an object are called <strong>methods</strong>). Each <C>new</C> object gets its own copy of the
          data. Inside a method, <C>this</C> means “the object I was called on”.
        </P>
        <Exercise id="class-intro" title="A counter" {...EX.classIntro} />
      </Step>

      <Step gate="point-class">
        <H>Build a Point</H>
        <P>
          A <C>constructor</C> is a special method that runs when you write <C>new Point(3, 4)</C>, so you can set up the
          object’s data. Finish the two methods.
        </P>
        <Exercise
          id="point-class"
          title="class Point"
          {...EX.pointClass}
          hints={[
            <>
              Work out <C>dx = other.x - this.x</C> and <C>dy = other.y - this.y</C> first.
            </>,
            <>
              Then <C>Math.sqrt(dx * dx + dy * dy)</C>. For add: <C>new Point(this.x + other.x, this.y + other.y)</C>.
            </>,
          ]}
        />
        <Callout kind="why">
          In Module 7 you’ll write a class called <C>Value</C>: a number that remembers how it was calculated, so it can work out
          its own gradient. It’s the engine of every neural network — and it’s about 60 lines, built from exactly what you just
          did.
        </Callout>
      </Step>

      <Step gate="q-this">
        <H>Code: done</H>
        <Quiz
          id="q-this"
          question={
            <>
              Inside a method, what does <C>this</C> refer to?
            </>
          }
          options={[
            { label: 'The class blueprint itself', why: 'The blueprint is shared; this is about one specific object.' },
            { label: 'The particular object the method was called on', correct: true },
            { label: 'The last object that was created', why: 'Each method call knows which object it belongs to, no matter when it was made.' },
          ]}
          explain="a.increment() changes a’s count, b.increment() changes b’s. this is whichever one is before the dot."
        />
        <P>
          That’s every piece of code you need: values, functions, arrays, loops, objects and classes. From here on, the code is
          the easy part — the ideas are the adventure. Next up: math, starting from the very beginning.
        </P>
      </Step>
    </Lesson>
  )
}
